import React, { useEffect, useMemo, useState } from "react";
import { MessageSquarePlus, Reply, X, Trash2, Check, Clock, Image as ImageIcon } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const ROLE_STYLES = {
    admin: { label: "Social Factory", chip: "bg-[#E5A93D]/20 text-[#E5A93D]" },
    marketing_manager: { label: "Marketing", chip: "bg-[#7C3AED]/20 text-[#7C3AED]" },
    agent: { label: "Makelaar", chip: "bg-emerald-500/20 text-emerald-600" },
    guest: { label: "Gast", chip: "bg-zinc-200 text-zinc-600" },
};

// Minimal comment thread without a video player. Shares the existing comment API
// using timestamp_seconds=0 + asset_type to scope comments to a non-video asset
// (e.g. AI photos). Supports replies and admin-resolve.
export default function AssetComments({ requestId, currentUser, assetType = "ai_photos", title = "Opmerkingen", emptyText = "Nog geen opmerkingen" }) {
    const [comments, setComments] = useState([]);
    const [text, setText] = useState("");
    const [posting, setPosting] = useState(false);
    const [replyTo, setReplyTo] = useState(null);
    const [replyText, setReplyText] = useState("");
    const [replyPosting, setReplyPosting] = useState(false);

    const fetchComments = async () => {
        try {
            const { data } = await api.get(`/requests/${requestId}/comments`);
            setComments(data.filter((c) => (c.asset_type || "preview") === assetType));
        } catch {}
    };
    useEffect(() => { fetchComments(); /* eslint-disable-next-line */ }, [requestId, assetType]);

    const { topLevel, repliesByParent } = useMemo(() => {
        const top = [];
        const rep = {};
        comments.forEach((c) => {
            if (c.parent_id) {
                rep[c.parent_id] = rep[c.parent_id] || [];
                rep[c.parent_id].push(c);
            } else {
                top.push(c);
            }
        });
        Object.keys(rep).forEach((k) => rep[k].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)));
        top.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        return { topLevel: top, repliesByParent: rep };
    }, [comments]);

    const submit = async () => {
        if (!text.trim()) return;
        setPosting(true);
        try {
            await api.post(`/requests/${requestId}/comments`, {
                timestamp_seconds: 0,
                text: text.trim(),
                asset_type: assetType,
            });
            setText("");
            await fetchComments();
            toast.success("Opmerking geplaatst");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fout");
        }
        setPosting(false);
    };

    const submitReply = async () => {
        if (!replyText.trim() || !replyTo) return;
        setReplyPosting(true);
        try {
            await api.post(`/requests/${requestId}/comments`, {
                timestamp_seconds: 0,
                text: replyText.trim(),
                asset_type: assetType,
                parent_id: replyTo.id,
            });
            setReplyText("");
            setReplyTo(null);
            await fetchComments();
            toast.success("Antwoord geplaatst");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fout");
        }
        setReplyPosting(false);
    };

    const deleteComment = async (cid) => {
        try {
            await api.delete(`/requests/${requestId}/comments/${cid}`);
            await fetchComments();
        } catch {}
    };
    const resolveComment = async (cid) => {
        try {
            await api.patch(`/requests/${requestId}/comments/${cid}/resolve`);
            await fetchComments();
        } catch {}
    };

    const openCount = topLevel.filter((c) => !c.resolved).length;

    return (
        <div className="mt-6" data-testid={`asset-comments-${assetType}`}>
            <div className="flex items-center gap-2 mb-3">
                <MessageSquarePlus className="h-4 w-4 text-[#E5A93D]" />
                <h3 className="font-display font-bold text-base tracking-tight">{title}</h3>
                {openCount > 0 && <span className="text-xs bg-[#E5A93D] text-black font-bold px-2 py-0.5 rounded-sm">{openCount} open</span>}
            </div>

            {/* New comment box */}
            <div className="mb-4 p-3 bg-[#FFFFFF] border border-zinc-200 rounded-sm">
                <textarea
                    rows={2}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Plaats een opmerking..."
                    className="w-full bg-[#FAFAFA] border border-zinc-200 rounded-sm p-2 text-sm text-zinc-800 focus:outline-none focus:border-[#E5A93D]"
                    data-testid={`asset-comment-input-${assetType}`}
                />
                <div className="flex items-center justify-end gap-2 mt-2">
                    <button
                        onClick={submit}
                        disabled={posting || !text.trim()}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold text-xs rounded-sm disabled:opacity-50 active:scale-[0.98]"
                        data-testid={`asset-comment-submit-${assetType}`}
                    >
                        <MessageSquarePlus className="h-3.5 w-3.5" />
                        {posting ? "Versturen..." : "Plaats opmerking"}
                    </button>
                </div>
            </div>

            {/* List */}
            {topLevel.length === 0 ? (
                <div className="text-sm text-zinc-500 py-6 text-center border border-dashed border-zinc-200 rounded-md">{emptyText}</div>
            ) : (
                <div className="space-y-3">
                    {topLevel.map((c) => (
                        <div key={c.id} data-testid={`asset-thread-${c.id}`}>
                            <Bubble comment={c} currentUser={currentUser} onDelete={deleteComment} onResolve={resolveComment} onReply={() => { setReplyTo(c); setReplyText(""); }} isReplyOpen={replyTo?.id === c.id} />
                            {(repliesByParent[c.id] || []).map((r) => (
                                <div key={r.id} className="mt-2 ml-8" data-testid={`asset-reply-${r.id}`}>
                                    <Bubble comment={r} currentUser={currentUser} onDelete={deleteComment} isReply />
                                </div>
                            ))}
                            {replyTo?.id === c.id && (
                                <div className="mt-2 ml-8 p-3 bg-[#FFFFFF] border border-zinc-200 rounded-sm">
                                    <textarea
                                        autoFocus
                                        value={replyText}
                                        onChange={(e) => setReplyText(e.target.value)}
                                        rows={2}
                                        placeholder={`Antwoord aan ${c.user_name}...`}
                                        className="w-full bg-[#FAFAFA] border border-zinc-200 rounded-sm p-2 text-sm text-zinc-800 focus:outline-none focus:border-[#E5A93D]"
                                    />
                                    <div className="flex items-center justify-end gap-2 mt-2">
                                        <button onClick={() => { setReplyTo(null); setReplyText(""); }} className="text-xs text-zinc-500 hover:text-zinc-950">Annuleer</button>
                                        <button
                                            onClick={submitReply}
                                            disabled={replyPosting || !replyText.trim()}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold text-xs rounded-sm disabled:opacity-50"
                                        >
                                            <Reply className="h-3 w-3" /> {replyPosting ? "..." : "Antwoord"}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function Bubble({ comment, currentUser, onDelete, onResolve, onReply, isReplyOpen, isReply = false }) {
    const role = comment.user_role || "agent";
    const style = ROLE_STYLES[role] || ROLE_STYLES.agent;
    const canDelete = currentUser && (currentUser.role === "admin" || currentUser.id === comment.user_id);
    const canReply = !!currentUser && !isReply && onReply;
    const [showAnnotated, setShowAnnotated] = useState(true);
    const hasPhoto = !!comment.photo_url;
    const hasAnnotation = !!comment.annotation_url;
    const annotationSrc = hasAnnotation
        ? `${process.env.REACT_APP_BACKEND_URL}${comment.annotation_url}`
        : null;
    return (
        <div className={`p-3 rounded-md border ${comment.resolved ? "bg-[#FFFFFF] border-zinc-100 opacity-70" : "bg-[#F4F4F5] border-zinc-200"}`}>
            <div className="flex items-start gap-3">
                {isReply && <Reply className="h-3 w-3 text-zinc-500 mt-1 flex-shrink-0" />}
                {hasPhoto && (
                    <a
                        href={comment.photo_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-shrink-0 relative block w-24 h-24 rounded-sm overflow-hidden border border-zinc-200 bg-zinc-50"
                        data-testid={`comment-photo-${comment.id}`}
                        title={typeof comment.photo_index === "number" ? `Foto ${comment.photo_index + 1}` : "Foto"}
                    >
                        <img
                            src={comment.photo_url}
                            alt={`Foto ${(comment.photo_index ?? 0) + 1}`}
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        {hasAnnotation && showAnnotated && (
                            <img
                                src={annotationSrc}
                                alt="Annotatie"
                                className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                            />
                        )}
                        {typeof comment.photo_index === "number" && (
                            <span className="absolute bottom-1 left-1 inline-flex items-center text-[10px] font-mono font-bold bg-black/70 text-white px-1.5 h-4 rounded-sm">
                                #{comment.photo_index + 1}
                            </span>
                        )}
                    </a>
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-medium text-zinc-950">{comment.user_name}</span>
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 rounded-sm ${style.chip}`}>{style.label}</span>
                        <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
                            <Clock className="h-3 w-3" />
                            {new Date(comment.created_at).toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit", year: "numeric" })}
                            <span className="text-zinc-400">•</span>
                            {new Date(comment.created_at).toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                        {comment.resolved && <span className="text-[10px] uppercase tracking-wider bg-emerald-500/20 text-emerald-600 px-1.5 rounded-sm">Opgelost</span>}
                    </div>
                    <p className="text-sm text-zinc-800 whitespace-pre-wrap break-words">{comment.text}</p>
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                        {hasAnnotation && (
                            <button
                                onClick={() => setShowAnnotated((v) => !v)}
                                className="text-xs text-[#E5A93D] hover:text-[#C8922C] flex items-center gap-1"
                                data-testid={`toggle-annotation-${comment.id}`}
                            >
                                <ImageIcon className="h-3 w-3" />
                                {showAnnotated ? "Verberg tekening" : "Toon tekening"}
                            </button>
                        )}
                        {hasAnnotation && (
                            <a
                                href={annotationSrc}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-zinc-500 hover:text-zinc-950"
                                data-testid={`open-annotation-${comment.id}`}
                            >
                                Tekening apart openen
                            </a>
                        )}
                        {canReply && (
                            <button onClick={onReply} className="text-xs text-zinc-500 hover:text-[#E5A93D] flex items-center gap-1">
                                {isReplyOpen ? <X className="h-3 w-3" /> : <Reply className="h-3 w-3" />}
                                {isReplyOpen ? "Annuleer" : "Antwoord"}
                            </button>
                        )}
                        {!comment.resolved && !isReply && currentUser?.role === "admin" && onResolve && (
                            <button onClick={() => onResolve(comment.id)} className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1">
                                <Check className="h-3 w-3" /> Opgelost
                            </button>
                        )}
                        {canDelete && (
                            <button onClick={() => onDelete(comment.id)} className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1">
                                <Trash2 className="h-3 w-3" /> Verwijder
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
